import _ from 'lodash';

/**
 * Specifies state-by-state options for the basic illustration,
 * and the states that enforce those options
 *
 * @remarks
 * Each option property is an array of two-letter uppercase
 * state abbreviations, each of which enforces that option.
 *
 * This allows for quick and easy checking by
 * ```StateSpecification.ExampleOption.includes(ExampleState)```
 *
 */
export const StateSpecification = {
  /**
   * Finds the proper state specification for this state, and then uses
   * that specification to choose the matching content set for that specification.
   *
   * @param state The state for this illustration
   * @param specificationGroup The `StateSpecification` property that contains sub-properties for multiple states
   * @param contentGroup The property that contains the different content options by state for a specific section, e.g. `IACMain.TextFillOptions`.
   */
  GetContentByState: (state: string, specificationGroup: SpecificationGroup, contentGroup: ContentOption[]) => {
    let optionForState = Object.keys(_.pickBy(specificationGroup, (option: string) => option.includes(state)))[0];
    optionForState = !!optionForState ? optionForState : 'Default';
    let optionsMap = _.keyBy(contentGroup, 'name');
    optionsMap = _.omit(optionsMap, ['name']);
    return optionsMap[optionForState];
  },

  /**
   * Option for whether to display the Interest Adjusted Cost section
   */
  IAC: [
    'AL',
    'AK',
    'AR',
    'AZ',
    'CA',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'PA',
    'RI',
    'SC',
    'SD',
    'TN',
    'TX',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ], // Excludes PR

  /**
   * Option for whether to display the Net Payment Cost Row (second row) in the IAC table
   */
  IACNetPaymentCostRow: [
    'AL',
    'AK',
    'AZ',
    'CO',
    'CT',
    'DC',
    'DE',
    'FL',
    'GA',
    'HI',
    'ID',
    'IL',
    'IN',
    'IA',
    'KS',
    'KY',
    'LA',
    'ME',
    'MD',
    'MA',
    'MI',
    'MN',
    'MS',
    'MO',
    'MT',
    'NE',
    'NV',
    'NH',
    'NJ',
    'NM',
    'NY',
    'NC',
    'ND',
    'OH',
    'OK',
    'OR',
    'RI',
    'SC',
    'SD',
    'TN',
    'UT',
    'VT',
    'VA',
    'WA',
    'WV',
    'WI',
    'WY',
  ], // Excludes AR, CA, PA, TX, PR

  /**
   * Option for whether to display the Equivalent Level Row (third row) in the IAC table
   */
  IACEquivalentLevelRow: ['AR', 'MA', 'PA'],

  /**
   * Option for whether to display premium payment options
   */
  NoPremiumPaymentOption: ['UT'],

  /**
   * Options for how to display the text in the IAC section
   */
  IACTextFillOptions: <SpecificationGroup>{
    /**
     * Option for whether to display a specific paragraph related to Interest Adjust Cost (IAC) for certain states
     */
    IACTextFillAlternative: ['ME', 'WA', 'UT'],

    /**
     * Option for how to display the IAC for PA
     */
    IACSurrenderComparisonPA: ['PA'],

    /**
     * Option for how to display the IAC for CA
     */
    IACSurrenderComparisonCA: ['CA'],

    /**
     * Option for how to display the IAC for TX
     */
    IACTextFillTX: ['TX'],

    /**
     * Option for how to display the IAC for AR
     */
    IACTextFillAR: ['AR'],
  },

  /**
   * Option for whether to include the Year 5 column in the IAC table
   */
  IACYear5Column: ['AR', 'CA', 'PA', 'TX'],

  /**
   * An additional info paragraph at the end of the IAC section, varies for Variable / Fixed loans
   */
  IACAdditionalInfoParagraph: ['NJ'],

  /**
   * Options for whether to display a different option for the signature page text
   */
  SignatureTextOptions: <SpecificationGroup>{
    /**
     * Option for Alaska
     */
    SignatureTextAK: ['AK'],

    /**
     * Option for Illinois
     */
    SignatureTextIL: ['IL'],

    /**
     * Option for New Jersey
     */
    SignatureTextNJ: ['NJ'],

    /**
     * Option for Texas
     */
    SignatureTextTX: ['TX'],
  },

  /**
   * An extra paragraph to the initial information
   */
  InitialInformationExtraParagraph: ['IL'],

  /**
   * Show license number of agent on cover page for these states
   */
  LicenseRequiredStates: ['AR', 'CA'],

  PhoneNumberRequiredStates: ['AK'],
};

export interface ContentOption {
  name: string;
}

type SpecificationGroup = {};
